// =====================================================================
// src/screens/v2/OrderChat.jsx
//
// V2 Order Chat screen — conversation between user and business.
//
// Key V2 elements:
//   - Order pin at top (linked to active order)
//   - System messages for order events (placed, confirmed, etc.)
//   - UPI payment card (business shares payment in chat — V2 spec)
//   - Quick action chips for common questions
//   - Typing indicator
//
// In Phase B, MOCK messages will be replaced by Firestore subcollection
// reads (chats/{orderId}/messages) with realtime updates.
//
// Props:
//   orderId      — order this chat is tied to
//   businessId   — business this chat is with
//   onBack       — return to previous screen
//   onOpenOrder  — open Order Detail
//   onOpenProfile — open Business Profile
// =====================================================================

import React, { useState, useRef, useEffect } from 'react';
import {
  ArrowLeft, MoreVertical, Phone, Paperclip, Send, Package,
  Clock, Plus, X, Loader2, Flag, ChevronLeft, Ban,
} from 'lucide-react';
import {
  doc, getDoc, collection, query, orderBy, onSnapshot,
  updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../lib/firebase';
import { sendChatMessage, submitOrderChatReport, blockUser, unblockUser, subscribeToBlockedUsers } from '../../lib/db';
import { getBusiness, logoBgFromCategory } from '../../lib/v2/firestore';
import './OrderChat.css';

// ============================================================
// CONSTANTS
// ============================================================

// Category → emoji for business logo (shared with Cart + OrderDetail + MerchantApp)
const CATEGORY_EMOJI = {
  food_beverages: '🍯',
  apparel: '👗',
  services: '🔧',
  groceries: '🛒',
  electronics: '📱',
  health_beauty: '💄',
  home_decor: '🏡',
};
function emojiForCategory(cat) {
  return CATEGORY_EMOJI[cat] || '🏪';
}

// ------------------------------------------------------------
// Session C2a — order-chat report reasons.
//
// Keys are REUSED from the existing vocabulary (V1's ReportUserSheet and
// C1's WALL_REPORT_REASONS) rather than invented fresh, so the super-admin
// Reports table doesn't end up with three different labels meaning "spam".
// Every key here is already mapped in super-admin/lib/reports.js.
//
// Order-specific wording only; a disagreement about an order is not the
// same thing as a bad post, so the copy is about the conversation.
// ------------------------------------------------------------
const ORDER_REPORT_REASONS = [
  { key: 'abuse',          label: 'Abusive or threatening messages' },
  { key: 'scam',           label: 'Scam or fraud attempt' },
  { key: 'spam',           label: 'Spam or unwanted messages' },
  { key: 'inappropriate',  label: 'Inappropriate content' },
  { key: 'impersonation',  label: 'Pretending to be someone else' },
  { key: 'other',          label: 'Something else' },
];

// Status pill text shown in the Order Pin (small subset of full status set)
const STATUS_PIN_LABEL = {
  placed:    'Verifying',
  confirmed: 'Confirmed',
  preparing: 'Preparing',
  ready:     'Ready',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

// Quick-chip set is status- and role-aware.
//   role   — 'customer' | 'merchant' (derived from currentUid vs order.customerId / business.ownerId)
//   status — order.status (placed | confirmed | preparing | ready | completed | cancelled)
//   mode   — order.mode   (delivery | pickup) — only branches the 'ready' stage
//   total  — order.total  (used by merchant 'Share UPI' / 'Payment received' prefills)
//
// Each chip:
//   { icon, text, prefill, variant? }
//   variant: 'cancel' renders the red pill style with a Lucide X icon (icon field ignored).
//
// Chips PREFILL the input box; they do not auto-send. User can edit before pressing Send.
function getQuickChips(role, status, mode, total) {
  const totalStr = `₹${(total || 0).toLocaleString('en-IN')}`;

  if (role === 'customer') {
    switch (status) {
      case 'placed':
        return [
          { icon: '📦', text: 'When confirmed?', prefill: 'When will the order be confirmed?' },
          { icon: '➕', text: 'Add to order?',   prefill: 'Can I add something to this order?' },
          { text: 'Cancel order?', prefill: 'Please cancel this order.', variant: 'cancel' },
        ];
      case 'confirmed':
        return [
          { icon: '⏱', text: 'When ready?',   prefill: 'When will it be ready?' },
          { icon: '💬', text: 'Any updates?',  prefill: 'Any updates on my order?' },
          { text: 'Cancel order?', prefill: 'Can I still cancel this order?', variant: 'cancel' },
        ];
      case 'preparing':
        return [
          { icon: '⏱', text: 'How long more?', prefill: 'How much longer?' },
          { icon: '💬', text: 'Any updates?',  prefill: 'Any updates on my order?' },
        ];
      case 'ready':
        if (mode === 'pickup') {
          return [
            { icon: '🚶', text: 'Coming to pick up', prefill: 'On my way to pick up.' },
          ];
        }
        return [
          { icon: '🛵', text: 'On the way?', prefill: 'Is it out for delivery?' },
          { icon: '📍', text: 'My address',  prefill: 'Sharing my delivery address: ' },
        ];
      case 'completed':
        return [
          { icon: '🙏', text: 'Thanks!', prefill: 'Thank you!' },
        ];
      case 'cancelled':
        return [
          { icon: '❓', text: 'Why cancelled?', prefill: 'Could you tell me why the order was cancelled?' },
        ];
      default:
        return [];
    }
  }

  // role === 'merchant'
  // Chips with `advanceTo` are DIRECT-SEND: one tap posts the canned
  // message AND moves the order to the named status. No editing step.
  // Optimized for the happy path — merchant taps once to confirm,
  // collect payment, mark ready, etc. Chips without `advanceTo` are
  // PREFILL — text goes into the input, merchant edits and sends.
  // Destructive transitions (Can't fulfill → cancelled) intentionally
  // stay as PREFILL so merchant can add a reason / soften the message.
  switch (status) {
    case 'placed':
      return [
        { icon: '✅', text: 'Confirming now',      prefill: 'Confirming your order now.',                            advanceTo: 'confirmed' },
        { icon: '❓', text: 'Need clarification', prefill: 'Quick question about your order: ' },
        { text: "Can't fulfill", prefill: "Sorry, we can't fulfill this order right now.", variant: 'cancel' },
      ];
    case 'confirmed':
      return [
        { icon: '💰', text: 'Share UPI',          prefill: `Please pay ${totalStr} via UPI to: ` },
        { icon: '✅', text: 'Payment received',   prefill: `Payment of ${totalStr} received. Thank you!`,           advanceTo: 'preparing' },
        { icon: '⏱', text: 'ETA 30 min',          prefill: 'Your order will be ready in approximately 30 minutes.' },
      ];
    case 'preparing':
      return [
        { icon: '⏱', text: 'Almost ready',       prefill: 'Your order is almost ready.' },
        { icon: '✅', text: 'Mark ready',         prefill: 'Your order is ready.',                                  advanceTo: 'ready' },
        { icon: '⏰', text: 'Running late',       prefill: 'Slight delay — should be ready in a few more minutes.' },
      ];
    case 'ready':
      if (mode === 'pickup') {
        return [
          { icon: '📦', text: 'Ready for pickup', prefill: 'Your order is ready for pickup.' },
          { icon: '📍', text: 'Where are you?',   prefill: 'Are you on your way?' },
          { icon: '🙏', text: 'Mark completed',   prefill: 'Thanks for picking up your order!',                     advanceTo: 'completed' },
        ];
      }
      return [
        { icon: '🛵', text: 'On the way',        prefill: 'Your order is out for delivery.' },
        { icon: '⏰', text: 'Running late',      prefill: 'Slight delay with the delivery — apologies.' },
        { icon: '🙏', text: 'Mark delivered',    prefill: 'Order delivered. Thanks!',                              advanceTo: 'completed' },
      ];
    case 'completed':
      return [
        { icon: '🙏', text: 'Thank you!',       prefill: 'Thanks for your order!' },
        { icon: '⭐', text: 'Feedback welcome', prefill: 'We hope you enjoyed it. Feedback is always welcome!' },
      ];
    case 'cancelled':
      return [
        { icon: '🙏', text: 'Apologies', prefill: 'Apologies for the inconvenience.' },
      ];
    default:
      return [];
  }
}

// ============================================================
// COMPONENT
// ============================================================

export default function OrderChat({ orderId, onBack, onOpenOrder, onOpenMerchantOrder, onOpenProfile }) {
  // ---- Auth ----
  const [currentUid, setCurrentUid] = useState(null);
  const [authResolved, setAuthResolved] = useState(false);

  // ---- Meta (order doc + business doc) ----
  const [orderDoc, setOrderDoc] = useState(null);
  const [businessDoc, setBusinessDoc] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [metaError, setMetaError] = useState(null);   // 'no-order' | 'no-business' | 'other'

  // ---- Messages ----
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(true);

  // ---- UI ----
  const [input, setInput] = useState('');   // 'order' | 'all' — 'all' deferred to V2.1
  const [toast, setToast] = useState(null);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // ---- Session C2a: report flow / C2b: block ----
  // sheet: null (closed) | 'menu' | 'reason' (reason picker)
  //        | 'confirm-block' (block confirmation)
  const [sheet, setSheet] = useState(null);
  // Users I have blocked. Needed here (rather than passed in) because V2
  // screens mount independently of V1's App tree. Tiny list, one subscription.
  const [myBlocks, setMyBlocks] = useState([]);
  const [blocking, setBlocking] = useState(false);
  const [reportReason, setReportReason] = useState(null);
  const [reportDetail, setReportDetail] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);

  // ---- Effect 0: Wait for Firebase Auth to resolve ----
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setCurrentUid(user?.uid || null);
      setAuthResolved(true);
    });
    return () => unsub();
  }, []);

  // ---- Effect 0b (Session C2b): my block list ----
  // Gates the composer and drives the Block/Unblock label. Only my OWN blocks
  // are knowable — the rules make the reverse direction unreadable on purpose,
  // so if THEY blocked ME this stays empty and the send simply fails.
  useEffect(() => {
    if (!currentUid) { setMyBlocks([]); return undefined; }
    const unsub = subscribeToBlockedUsers(currentUid, (list) => setMyBlocks(list || []));
    return () => unsub();
  }, [currentUid]);

  // ---- Effect 1: Load order doc + business doc ----
  useEffect(() => {
    if (!orderId) {
      setLoadingMeta(false);
      setMetaError('no-order');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingMeta(true);
        const orderSnap = await getDoc(doc(db, 'orders', orderId));
        if (cancelled) return;
        if (!orderSnap.exists()) {
          setMetaError('no-order');
          setLoadingMeta(false);
          return;
        }
        const order = { id: orderSnap.id, ...orderSnap.data() };
        setOrderDoc(order);

        if (!order.businessId) {
          setMetaError('no-business');
          setLoadingMeta(false);
          return;
        }
        const biz = await getBusiness(order.businessId);
        if (cancelled) return;
        if (!biz) {
          setMetaError('no-business');
          setLoadingMeta(false);
          return;
        }
        setBusinessDoc(biz);
        setMetaError(null);
        setLoadingMeta(false);
      } catch (err) {
        console.error('[OrderChat] meta load failed:', err);
        if (!cancelled) {
          setMetaError('other');
          setLoadingMeta(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [orderId]);

  // ---- Effect 2: Subscribe to /chats/{orderId}/messages live ----
  // The chat doc itself is created on first send (via sendChatMessage's
  // setDoc-with-merge). The messages subcollection just stays empty until
  // someone sends. onSnapshot still works on an empty subcollection.
  useEffect(() => {
    if (!authResolved || !currentUid || !orderId) return undefined;
    setLoadingMessages(true);
    const q = query(
      collection(db, 'chats', orderId, 'messages'),
      orderBy('sentAt', 'asc'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setMessages(list);
        setLoadingMessages(false);
      },
      (err) => {
        console.error('[OrderChat] messages subscription failed:', err);
        setLoadingMessages(false);
      }
    );
    return () => unsub();
  }, [authResolved, currentUid, orderId]);

  // ---- Toast auto-dismiss ----
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  // ---- Auto-scroll to bottom on new messages ----
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // ---- Derived: counterparty (the OTHER participant) ----
  // If I'm the customer, counterparty is the business owner.
  // If I'm the merchant (business owner), counterparty is the customer.
  const counterpartyId = (() => {
    if (!orderDoc || !currentUid) return null;
    if (currentUid === orderDoc.customerId) {
      return businessDoc?.ownerId || null;
    }
    if (currentUid === businessDoc?.ownerId) {
      return orderDoc.customerId;
    }
    return null;   // user is neither — rules will deny anyway
  })();

  // ---- Derived (Session C2b): have I blocked the other party? ----
  // Only ever answers about MY OWN blocks. If they blocked me, this is false
  // and the composer stays live — the send just fails with neutral copy. That
  // asymmetry is deliberate: telling someone they've been blocked is what
  // moves the problem offline.
  const iBlockedThem = !!counterpartyId && myBlocks.some((b) => b.id === counterpartyId);

  // ---- Derived: role (drives the quick-action chip set) ----
  const role = (() => {
    if (!orderDoc || !currentUid || !businessDoc) return null;
    // Owner check FIRST: if the same UID is somehow both the order's customer
    // and the business owner (only reachable in test data — the app blocks an
    // owner from ordering their own business), the owner view is the correct
    // one. Checking customerId first would trap them in the customer view.
    if (currentUid === businessDoc.ownerId)     return 'merchant';
    if (currentUid === orderDoc.customerId)     return 'customer';
    return null;
  })();

  // ---- Derived: status-aware quick chips ----
  const quickChips = (role && orderDoc)
    ? getQuickChips(role, orderDoc.status || 'placed', orderDoc.mode, orderDoc.total)
    : [];

  // ---- Derived: business header display ----
  const business = (() => {
    if (!businessDoc) {
      return {
        name: 'Loading…',
        logo: '🏪',
        logoBg: 'linear-gradient(135deg, #B8893A 0%, #D6B576 100%)',
        status: '',
        initial: '?',
      };
    }
    return {
      id: businessDoc.id,
      name: businessDoc.name || 'Business',
      logo: emojiForCategory(businessDoc.category),
      logoBg: logoBgFromCategory(businessDoc.category),
      status: businessDoc.status === 'verified' ? '✓ Verified' : '',
      initial: (businessDoc.name || '?').charAt(0).toUpperCase(),
    };
  })();

  // ---- Derived: order pin display ----
  const order = (() => {
    if (!orderDoc) return null;
    return {
      id: orderDoc.displayNumber || orderDoc.id,
      docId: orderDoc.id,
      itemsCount: Array.isArray(orderDoc.items) ? orderDoc.items.length : 0,
      total: orderDoc.total || 0,
      status: orderDoc.status || 'placed',
      statusLabel: STATUS_PIN_LABEL[orderDoc.status] || orderDoc.status,
    };
  })();

  async function handleSend() {
    const text = input.trim();
    if (!text || sending) return;
    if (!currentUid) {
      setToast('Please sign in to send messages');
      return;
    }
    if (!counterpartyId) {
      setToast('Cannot determine recipient yet');
      return;
    }
    setSending(true);
    setInput('');   // clear immediately for responsiveness
    try {
      // sendChatMessage in db.js does the heavy lifting:
      //   1. setDoc(/chats/{chatId}) with merge:true — creates or updates
      //      with participants, requesterId, lastMessage, lastMessageAt, lastReadAt
      //   2. addDoc(/chats/{chatId}/messages) with { fromId, text, sentAt }
      // The deployed onChatMessageCreated Cloud Function will then push a
      // notification to counterpartyId.
      await sendChatMessage({
        chatId: orderId,
        fromId: currentUid,
        toId: counterpartyId,
        text,
      });
    } catch (err) {
      // Session C2b — do NOT surface err.code here. A block denies the write,
      // and echoing 'permission-denied' at the sender tells them they've been
      // blocked, which is exactly what escalates an in-app problem into an
      // offline one. Neutral copy, indistinguishable from a network failure;
      // the real error still goes to the console for us. V1's handleSendMessage
      // already behaves this way — this brings V2 in line.
      console.error('[OrderChat] send failed:', err);
      setInput(text);   // restore on failure so user can retry
      setToast(err?.code === 'permission-denied'
        ? "Message couldn't be delivered"
        : `Couldn't send: ${err.code || err.message || 'try again'}`);
    } finally {
      setSending(false);
    }
  }

  function handleQuickChip(chip) {
    // Direct-send path: chip has `advanceTo`, so post the canned message
    // AND advance order status in one tap. Editing is skipped — the
    // merchant has committed by tapping the chip. Useful for the happy
    // path (Confirming now, Payment received, Mark ready, Mark completed)
    // where merchants do these many times and a two-step flow is friction.
    if (chip.advanceTo) {
      handleDirectSendAndAdvance(chip.prefill, chip.advanceTo);
      return;
    }
    // Otherwise prefill the input so the merchant can edit (Share UPI
    // needs a UPI ID added, ETA messages get edited times, etc.).
    setInput(chip.prefill);
  }

  // Direct-send + status-advance combined. Used by the happy-path quick
  // chips with `advanceTo` set. Posts the message first, then updates
  // the order. If either step fails the merchant gets a toast and can
  // retry — partial failures are acceptable (a message without status
  // bump is better than no message at all).
  async function handleDirectSendAndAdvance(text, newStatus) {
    if (!text || sending) return;
    if (!currentUid) {
      setToast('Please sign in');
      return;
    }
    if (!counterpartyId) {
      setToast('Cannot determine recipient yet');
      return;
    }
    setSending(true);
    try {
      // Step 1: post the canned message.
      await sendChatMessage({
        chatId: orderId,
        fromId: currentUid,
        toId: counterpartyId,
        text,
      });
      // Step 2: advance order status. Mirrors MerchantApp.updateOrderStatus
      // behaviour (timestamp on the new-status field). Skipping the
      // cancellation reason path here since the cancel chip stays as a
      // prefill — only forward transitions go through this helper.
      const orderRef = doc(db, 'orders', orderId);
      const updates = { status: newStatus };
      if (newStatus === 'confirmed') updates.confirmedAt = serverTimestamp();
      if (newStatus === 'preparing') updates.preparingAt = serverTimestamp();
      if (newStatus === 'ready') updates.readyAt = serverTimestamp();
      if (newStatus === 'completed') updates.completedAt = serverTimestamp();
      await updateDoc(orderRef, updates);
      // Order's status field is read via the realtime listener; chip set
      // refreshes automatically on the next render.
    } catch (err) {
      // Same neutral-copy rule as handleSend — see the note there.
      console.error('[OrderChat] direct-send + advance failed:', err);
      setToast(err?.code === 'permission-denied'
        ? "Message couldn't be delivered"
        : `Couldn't update: ${err.code || err.message || 'try again'}`);
    } finally {
      setSending(false);
    }
  }

  // ---- Session C2a: reporting ----------------------------------------
  // Play requires an in-app report option on user-to-user messaging
  // surfaces, and Aangan's terms already promise one. Both sides of an
  // order chat can file: the customer against the business owner, the
  // merchant against the customer. `role` and `counterpartyId` are already
  // derived above, so nothing new needs plumbing.
  //
  // NOT included here: Block. That needs a real collection, rules, and
  // enforcement across every surface where the blocked party's content can
  // appear — it is its own session (C2b), not a bolt-on to this menu.

  function closeSheet() {
    setSheet(null);
    setReportReason(null);
    setReportDetail('');
  }

  // ---- Session C2b: block / unblock ----------------------------------
  // Block targets the PERSON, not the business. For a customer that means the
  // owner — so it also covers any other business they run, which the confirm
  // copy says out loud rather than doing quietly. Blocking a businessId would
  // leave the same owner free to reach the same customer from their personal
  // V1 account: different surface, same human.
  //
  // name is written empty because neither the order nor the business doc
  // carries the counterparty's name, and fetching it just to label a block
  // would be a read spent on nothing. The Blocked users screen hydrates names
  // from /users on demand for exactly this case.
  async function handleBlock() {
    if (blocking || !currentUid || !counterpartyId) return;
    setBlocking(true);
    try {
      await blockUser({ blockerId: currentUid, blockedId: counterpartyId, name: '', photoUrl: null });
      closeSheet();
      setToast(role === 'merchant' ? 'Customer blocked' : 'Blocked');
    } catch (err) {
      console.error('[OrderChat] block failed:', err);
      setToast('Could not block — try again');
    } finally {
      setBlocking(false);
    }
  }

  async function handleUnblock() {
    if (blocking || !currentUid || !counterpartyId) return;
    setBlocking(true);
    try {
      await unblockUser({ blockerId: currentUid, blockedId: counterpartyId });
      closeSheet();
      setToast('Unblocked');
    } catch (err) {
      console.error('[OrderChat] unblock failed:', err);
      setToast('Could not unblock — try again');
    } finally {
      setBlocking(false);
    }
  }

  async function handleSubmitReport() {
    if (submittingReport || !reportReason) return;
    if (!currentUid) {
      setToast('Please sign in to report');
      return;
    }
    if (!counterpartyId || !orderDoc?.businessId) {
      setToast('Cannot file this report yet — try reopening the chat');
      return;
    }
    setSubmittingReport(true);
    try {
      const res = await submitOrderChatReport({
        orderId,
        businessId: orderDoc.businessId,
        reportedUserId: counterpartyId,
        reporterId: currentUid,
        // displayName rather than a /users/{uid} read: the admin drawer
        // resolves the reporter's real name from /users anyway, so this is
        // a convenience copy, not a source of truth. Not worth a read on
        // every report.
        reporterName: auth.currentUser?.displayName || '',
        reporterRole: role || 'customer',
        reason: reportReason,
        detail: reportDetail,
      });
      closeSheet();
      setToast(res?.duplicate
        ? 'Already reported — we\'re reviewing.'
        : 'Reported. We\'ll review within 24 hours.');
    } catch (err) {
      console.error('[OrderChat] report failed:', err);
      setToast(`Couldn't report: ${err.code || err.message || 'try again'}`);
    } finally {
      setSubmittingReport(false);
    }
  }

  function handleCopyUpi(upiId) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(upiId).then(
        () => setToast(`UPI ID copied · ${upiId}`),
        () => setToast(`UPI: ${upiId}`)
      );
    } else {
      setToast(`UPI: ${upiId}`);
    }
  }

  function handlePayUpi(amount) {
    setToast(`Opens UPI app to pay ₹${amount.toLocaleString('en-IN')}`);
  }

  // ---- Early returns for loading/error states ----
  if (loadingMeta) {
    return (
      <div className="v2-order-chat">
        <div className="v2oc-top-bar">
          <button className="v2oc-back-btn" onClick={onBack} aria-label="Back">
            <ArrowLeft size={20} />
          </button>
          <div style={{ flex: 1 }} />
        </div>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Loader2 size={32} className="v2oc-spin" />
        </div>
      </div>
    );
  }

  if (metaError) {
    return (
      <div className="v2-order-chat">
        <div className="v2oc-top-bar">
          <button className="v2oc-back-btn" onClick={onBack} aria-label="Back">
            <ArrowLeft size={20} />
          </button>
          <div className="v2oc-biz-info">
            <div className="v2oc-biz-name">Chat unavailable</div>
          </div>
        </div>
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted, #6E6E78)' }}>
          {metaError === 'no-order' && 'This order doesn\'t exist or you don\'t have access.'}
          {metaError === 'no-business' && 'The business for this order is unavailable.'}
          {metaError === 'other' && 'Couldn\'t load the chat. Please try again later.'}
        </div>
      </div>
    );
  }

  return (
    <div className="v2-order-chat">
      {/* ============ TOP BAR ============ */}
      <div className="v2oc-top-bar">
        <button className="v2oc-back-btn" onClick={onBack} aria-label="Back">
          <ArrowLeft size={20} />
        </button>
        <div
          className="v2oc-biz-mini"
          onClick={() => onOpenProfile?.(business.id) || setToast('Opens business profile')}
        >
          <div className="v2oc-biz-logo" style={{ background: business.logoBg }}>
            {business.logo}
          </div>
          <div className="v2oc-biz-info">
            <div className="v2oc-biz-name">{business.name}</div>
            <div className="v2oc-biz-status">{business.status}</div>
          </div>
        </div>
        <button
          className="v2oc-icon-btn"
          onClick={() => setToast('Call · gated until business activates Connect')}
          aria-label="Call"
        >
          <Phone size={18} />
        </button>
        <button
          className="v2oc-icon-btn"
          onClick={() => setSheet('menu')}
          aria-label="More"
        >
          <MoreVertical size={18} />
        </button>
      </div>

      {/* ============ ORDER PIN ============ */}
      <div
        className="v2oc-order-pin"
        onClick={() => {
          // Path Z Commit 2: merchant viewing this chat doesn't want
          // the customer-flavored OrderDetail. Route them to
          // MerchantApp instead, which auto-opens its richer
          // OrderDetailOverlay (status transitions, cancel, etc).
          // Customer continues to the standard OrderDetail.
          if (role === 'merchant') {
            onOpenMerchantOrder?.(order.docId)
              || setToast(`Open in dashboard · #${order.id}`);
          } else {
            onOpenOrder?.(order.docId)
              || setToast(`Opens Order Detail #${order.id}`);
          }
        }}
      >
        <div className="v2oc-order-pin-icon">
          <Package size={18} />
        </div>
        <div className="v2oc-order-pin-content">
          <div className="v2oc-order-pin-id">
            #{order.id} · {order.itemsCount} {order.itemsCount === 1 ? 'item' : 'items'} · ₹{order.total.toLocaleString('en-IN')}
          </div>
          <div className="v2oc-order-pin-line">
            <span className="v2oc-order-pin-pill">{order.statusLabel}</span>
          </div>
        </div>
        <div className="v2oc-order-pin-cta">View →</div>
      </div>

      {/* ============ SCOPE STRIP ============ */}

      {/* ============ CHAT SCROLL ============ */}
      <div className="v2oc-chat-scroll" ref={scrollRef}>
        {loadingMessages ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <Loader2 size={24} className="v2oc-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="v2oc-day-divider">
            <span>Send the first message about this order</span>
          </div>
        ) : (
          <>
            <div className="v2oc-day-divider">
              <span>Today</span>
            </div>
            {messages.map((msg) => {
              const isMine = msg.fromId === currentUid;
              if (isMine) {
                return (
                  <div key={msg.id} className="v2oc-msg v2oc-msg-sent">
                    <div className="v2oc-msg-bubble">{msg.text}</div>
                  </div>
                );
              }
              return (
                <div key={msg.id} className="v2oc-msg v2oc-msg-received">
                  <div className="v2oc-msg-avatar">{business.initial}</div>
                  <div className="v2oc-msg-bubble">{msg.text}</div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* ============ QUICK ACTIONS ============ */}
      {/* Chips are status- and role-aware. See getQuickChips() at top of file.
          Hidden entirely if no chips apply (e.g. role unresolved). */}
      {quickChips.length > 0 && (
        <div className="v2oc-quick-actions">
          {quickChips.map((chip, idx) => (
            <button
              key={idx}
              className={`v2oc-quick-chip ${chip.variant === 'cancel' ? 'v2oc-quick-chip-cancel' : ''}`}
              onClick={() => handleQuickChip(chip)}
            >
              {chip.variant === 'cancel'
                ? <X size={11} />
                : <span>{chip.icon}</span>}
              {' '}{chip.text}
            </button>
          ))}
        </div>
      )}

      {/* ============ INPUT BAR ============ */}
      {/* Session C2b — if I blocked them, the composer is replaced rather than
          left live. Every send would be denied by the rules anyway, and an
          input that silently always fails is worse than no input. This only
          fires for MY OWN blocks; if THEY blocked ME the composer stays, the
          send fails, and the toast stays neutral — see handleSend. */}
      {iBlockedThem ? (
        <div className="v2oc-input-bar" style={{ display: 'block', padding: '12px 16px' }}>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, textAlign: 'center', color: 'var(--text-muted, #6E6E78)' }}>
            You blocked {role === 'merchant' ? 'this customer' : 'this business owner'}.{' '}
            <button
              type="button"
              onClick={handleUnblock}
              disabled={blocking}
              style={{
                border: 'none', background: 'transparent', padding: 0,
                font: 'inherit', fontSize: 13, fontWeight: 600,
                cursor: blocking ? 'default' : 'pointer',
                color: 'var(--accent, #B8893A)', textDecoration: 'underline',
              }}
            >
              {blocking ? 'Unblocking…' : 'Unblock to message'}
            </button>
          </p>
        </div>
      ) : (
      <div className="v2oc-input-bar">
        <button
          className="v2oc-attach-btn"
          onClick={() => setToast('Attach: Camera · Gallery · Document')}
          aria-label="Attach"
        >
          <Paperclip size={18} />
        </button>
        <textarea
          className="v2oc-input-field"
          placeholder="Type a message…"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          rows={1}
        />
        <button
          className={`v2oc-send-btn ${input.trim() && !sending ? 'active' : ''}`}
          onClick={handleSend}
          disabled={!input.trim() || sending}
          aria-label="Send"
        >
          {sending ? <Loader2 size={16} className="v2oc-spin" /> : <Send size={16} />}
        </button>
      </div>
      )}

      {/* ============ REPORT SHEET (Session C2a) ============ */}
      {/* Inline styles rather than new v2oc- classes: this sheet is the only
          consumer, and OrderChat.css isn't touched by this commit. The one
          class used is the SHARED `sheet-bottom-safe` — Session C3 fixes the
          Cancel-clipped-behind-the-nav bug on that class, and using it here
          means this sheet inherits the fix rather than needing its own. */}
      {sheet && (
        <>
          <div
            onClick={closeSheet}
            style={{
              position: 'absolute', inset: 0, zIndex: 40,
              background: 'rgba(0,0,0,0.4)',
            }}
          />
          <div
            className="sheet-bottom-safe"
            role="dialog"
            aria-modal="true"
            aria-label={sheet === 'menu' ? 'Chat options' : 'Report this order chat'}
            style={{
              position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 41,
              background: 'var(--surface, #FFFFFF)',
              borderTopLeftRadius: 18, borderTopRightRadius: 18,
              boxShadow: '0 -8px 32px rgba(0,0,0,0.18)',
              maxHeight: '78%', overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
              <div style={{
                width: 36, height: 4, borderRadius: 2,
                background: 'var(--border, #E4E0D8)',
              }} />
            </div>

            {sheet === 'menu' ? (
              <div style={{ padding: '10px 12px 4px' }}>
                <button
                  type="button"
                  onClick={() => setSheet('reason')}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                    padding: '14px 12px', borderRadius: 12, border: 'none',
                    background: 'transparent', cursor: 'pointer',
                    font: 'inherit', fontSize: 15, textAlign: 'left',
                    color: 'var(--danger, #C0392B)',
                  }}
                >
                  <Flag size={18} />
                  Report this order chat
                </button>
                {/* Session C2b — Block. Unblock is immediate (it only undoes
                    something); Block gets a confirm step, because it also stops
                    new orders and reaches the owner's other businesses. */}
                <button
                  type="button"
                  onClick={() => (iBlockedThem ? handleUnblock() : setSheet('confirm-block'))}
                  disabled={!counterpartyId || blocking}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                    padding: '14px 12px', borderRadius: 12, border: 'none',
                    background: 'transparent',
                    cursor: counterpartyId && !blocking ? 'pointer' : 'default',
                    opacity: counterpartyId && !blocking ? 1 : 0.45,
                    font: 'inherit', fontSize: 15, textAlign: 'left',
                    color: iBlockedThem ? 'var(--text, #1E1E24)' : 'var(--danger, #C0392B)',
                  }}
                >
                  <Ban size={18} />
                  {iBlockedThem
                    ? 'Unblock'
                    : role === 'merchant' ? 'Block this customer' : 'Block this business owner'}
                </button>
                <button
                  type="button"
                  onClick={closeSheet}
                  style={{
                    width: '100%', padding: '14px 12px', marginTop: 4,
                    borderRadius: 12, border: 'none', background: 'transparent',
                    cursor: 'pointer', font: 'inherit', fontSize: 15,
                    color: 'var(--text-muted, #6E6E78)',
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : sheet === 'confirm-block' ? (
              <div style={{ padding: '10px 16px 4px' }}>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, color: 'var(--text, #1E1E24)' }}>
                  {role === 'merchant'
                    ? 'Block this customer?'
                    : `Block the owner of ${business.name}?`}
                </div>
                <p style={{ fontSize: 13, lineHeight: 1.55, margin: '0 0 14px', color: 'var(--text-muted, #6E6E78)' }}>
                  {role === 'merchant'
                    ? "You won't be able to message each other, and they can't place new orders with your businesses. Any order already in progress can still be completed or cancelled."
                    : `You won't be able to message each other, and you can't place new orders with ${business.name} — or with any other business the same owner runs. An order already in progress can still be completed or cancelled.`}
                  {' '}They can still see your posts and batches.
                </p>
                <button
                  type="button"
                  onClick={handleBlock}
                  disabled={blocking}
                  style={{
                    width: '100%', padding: '13px 12px', borderRadius: 12, border: 'none',
                    font: 'inherit', fontSize: 15, fontWeight: 600,
                    cursor: blocking ? 'default' : 'pointer', opacity: blocking ? 0.45 : 1,
                    background: 'var(--danger, #C0392B)', color: '#FFFFFF',
                  }}
                >
                  {blocking ? 'Blocking…' : 'Block'}
                </button>
                <button
                  type="button"
                  onClick={() => setSheet('menu')}
                  style={{
                    width: '100%', padding: '13px 12px', marginTop: 4,
                    borderRadius: 12, border: 'none', background: 'transparent',
                    cursor: 'pointer', font: 'inherit', fontSize: 15,
                    color: 'var(--text-muted, #6E6E78)',
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div style={{ padding: '10px 16px 4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <button
                    type="button"
                    onClick={() => { setSheet('menu'); setReportReason(null); setReportDetail(''); }}
                    aria-label="Back"
                    style={{
                      border: 'none', background: 'transparent', cursor: 'pointer',
                      padding: 4, display: 'flex', color: 'var(--text-muted, #6E6E78)',
                    }}
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text, #1E1E24)' }}>
                    Why are you reporting?
                  </div>
                </div>
                <p style={{
                  fontSize: 12, lineHeight: 1.5, margin: '0 0 12px 28px',
                  color: 'var(--text-muted, #6E6E78)',
                }}>
                  This goes to the Aangan team, not to{' '}
                  {role === 'merchant' ? 'the customer' : business.name}. We review within 24 hours.
                </p>

                {ORDER_REPORT_REASONS.map((r) => {
                  const active = reportReason === r.key;
                  return (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => setReportReason(r.key)}
                      style={{
                        width: '100%', textAlign: 'left', marginBottom: 6,
                        padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                        font: 'inherit', fontSize: 14,
                        color: 'var(--text, #1E1E24)',
                        border: `1px solid ${active ? 'var(--accent, #B8893A)' : 'var(--border, #E4E0D8)'}`,
                        background: active ? 'var(--accent-soft, #FBF6EC)' : 'transparent',
                      }}
                    >
                      {r.label}
                    </button>
                  );
                })}

                <textarea
                  value={reportDetail}
                  onChange={(e) => setReportDetail(e.target.value.slice(0, 500))}
                  placeholder="Add any detail (optional)"
                  rows={3}
                  style={{
                    width: '100%', marginTop: 6, padding: '10px 12px',
                    borderRadius: 10, border: '1px solid var(--border, #E4E0D8)',
                    font: 'inherit', fontSize: 14, resize: 'none',
                    color: 'var(--text, #1E1E24)', background: 'transparent',
                    boxSizing: 'border-box',
                  }}
                />

                <button
                  type="button"
                  onClick={handleSubmitReport}
                  disabled={!reportReason || submittingReport}
                  style={{
                    width: '100%', marginTop: 10, padding: '13px 12px',
                    borderRadius: 12, border: 'none',
                    font: 'inherit', fontSize: 15, fontWeight: 600,
                    cursor: reportReason && !submittingReport ? 'pointer' : 'default',
                    opacity: reportReason && !submittingReport ? 1 : 0.45,
                    background: 'var(--danger, #C0392B)', color: '#FFFFFF',
                  }}
                >
                  {submittingReport ? 'Submitting…' : 'Submit report'}
                </button>
                <button
                  type="button"
                  onClick={closeSheet}
                  style={{
                    width: '100%', padding: '13px 12px', marginTop: 4,
                    borderRadius: 12, border: 'none', background: 'transparent',
                    cursor: 'pointer', font: 'inherit', fontSize: 15,
                    color: 'var(--text-muted, #6E6E78)',
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* ============ TOAST ============ */}
      {toast && <div className="v2oc-toast">{toast}</div>}
    </div>
  );
}
